import React, { useState } from "react";
import { Create } from "react-admin";
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CouponType } from "../../enums/CouponType";
import { ProductType } from "../../enums/ProductType";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { useNavigate } from "react-router-dom";
import { CouponSkuService } from "../../services/CouponSkuService";
import { TimeEnum } from "../../enums/TimeEnum";
import { useSlotSizeOptions } from "../../utils/SlotSizeOptions";

interface SkuData {
  name: string;
  description: string;
  price: string;
  value: string;
  couponType: string;
  numberOfDays: number;
  productType: string;
  minimumOrderValue: string;
  maximumDiscountAmount: string;
  maximumUsageLimit: number;
  noOfPeople?: number;
  slotSize?: number | null;
  isActive?: boolean;
  isCustomCombo?: boolean;
  isRenewable?: boolean | null;
  packTag?: string;
}

const couponSkuService = new CouponSkuService();

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#6C7C93", letterSpacing: "0.5px", textTransform: "uppercase", mb: 1.5, fontFamily: "inherit" }}>
    {children}
  </Typography>
);

const CreateCouponSku = () => {
  const [skuData, setSkuData] = useState<SkuData>({
    name: "",
    description: "",
    price: "",
    value: "",
    couponType: CouponType.FLAT,
    numberOfDays: 0,
    productType: ProductType.ONE_MEAL_SERVICE,
    minimumOrderValue: "",
    maximumDiscountAmount: "",
    maximumUsageLimit: 0,
    noOfPeople: 0,
    slotSize: null,
    isActive: true,
    isRenewable: null,
    packTag: "",
  });
  const navigate = useNavigate();
  const [timeInclusionExclusion, setTimeInclusionExclusion] = useState<boolean>(false);
  const [addTimeInclusion, setAddTimeInclusion] = useState<boolean>(false);
  const [selectedTimeEnums, setSelectedTimeEnums] = useState<any>([]);
  const slotSizeOptions = useSlotSizeOptions();

  const validate = () => {
    const errors: any = {};
    if (!skuData.name) errors.couponName = "Coupon sku name is required";
    if (!skuData.description) errors.couponDescription = "Coupon sku description is required";
    if (Number(skuData.value) < 0) errors.couponValue = "Wrong value for coupon sku";
    if (Number(skuData.price) < 0) errors.couponPrice = "Wrong sku price";
    if (!skuData.couponType) errors.couponType = "Coupon type is required";
    if (Number(skuData.numberOfDays) < 0) errors.numberOfDays = "Number of days must be a positive number";
    if (!skuData.productType) errors.productType = "Product type is required";
    if (!skuData.minimumOrderValue) errors.minimumOrderValue = "Minimum order value is required";
    if (Number(skuData.minimumOrderValue) < 0) errors.minimumOrderValue = "Minimum order value must be a positive number";
    if (!skuData.maximumDiscountAmount) errors.maximumDiscountAmount = "Maximum discount amount is required";
    if (Number(skuData.maximumDiscountAmount) < 0) errors.maximumDiscountAmount = "Maximum discount amount must be a positive number";
    if (!skuData.maximumUsageLimit) errors.maximumUsageLimit = "Maximum usage limit is required";
    if (Number(skuData.maximumUsageLimit) < 0) errors.maximumUsageLimit = "Maximum usage limit must be a positive number";
    return errors;
  };

  const handleSubmit = () => {
    if (!validate()) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Please fill all required fields",
        type: CustomSnackbarTypes.ERROR,
      });
      return;
    }

    const getTimeData = () => {
      if (selectedTimeEnums.length > 0) {
        return { type: addTimeInclusion ? "inclusion" : "exclusion", timeSlots: selectedTimeEnums };
      }
      return null;
    };

    const payload = {
      name: skuData.name,
      description: skuData.description,
      price: skuData.price,
      value: skuData.value,
      couponType: skuData.couponType,
      numberOfDays: Number(skuData.numberOfDays),
      productType: skuData.productType,
      minimumOrderValue: skuData.minimumOrderValue,
      maximumDiscountAmount: skuData.maximumDiscountAmount,
      maximumUsageLimit: Number(skuData.maximumUsageLimit),
      isActive: skuData.isActive === undefined ? true : skuData.isActive,
      noOfPeople: Number(skuData.noOfPeople) || 0,
      slotSize: skuData.slotSize ?? null,
      timeInclusionExclusionRule: timeInclusionExclusion ? getTimeData() : null,
      isCustomCombo: false,
      isRenewable: skuData.isRenewable ?? null,
      packTag: skuData.packTag || null,
    };

    couponSkuService.createCouponSku(payload).then((res) => {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Coupon SKU created successfully",
        type: CustomSnackbarTypes.SUCCESS,
      });
      navigate(`/CouponSku/${res?.id}/show`);
    }).catch(() => {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        type: CustomSnackbarTypes.ERROR,
      });
    });
  };

  return (
    <Create title="Create Coupon SKU">
      <Box sx={{ padding: "24px", display: "flex", flexDirection: "column", gap: "24px" }}>

        {/* ── Text Fields ── */}
        <Box>
          <SectionLabel>Basic Information</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <TextField
              label="Name"
              value={skuData.name}
              onChange={(e) => setSkuData({ ...skuData, name: e.target.value })}
            />
            <TextField
              label="Description"
              value={skuData.description}
              onChange={(e) => setSkuData({ ...skuData, description: e.target.value })}
            />
            <TextField
              label="Pack Tag"
              value={skuData.packTag}
              onChange={(e) => setSkuData({ ...skuData, packTag: e.target.value })}
            />
          </Box>
        </Box>

        <Box>
          <SectionLabel>Pricing & Limits</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <TextField
              label="Price"
              value={skuData.price}
              onChange={(e) => setSkuData({ ...skuData, price: e.target.value })}
            />
            <TextField
              label="Value"
              value={skuData.value}
              onChange={(e) => setSkuData({ ...skuData, value: e.target.value })}
            />
            <TextField
              label="Minimum Order Value"
              value={skuData.minimumOrderValue}
              onChange={(e) => setSkuData({ ...skuData, minimumOrderValue: e.target.value })}
            />
            <TextField
              label="Maximum Discount Amount"
              value={skuData.maximumDiscountAmount}
              onChange={(e) => setSkuData({ ...skuData, maximumDiscountAmount: e.target.value })}
            />
            <TextField
              label="Maximum Usage Limit"
              type="number"
              value={skuData.maximumUsageLimit}
              onChange={(e) => setSkuData({ ...skuData, maximumUsageLimit: Number(e.target.value) })}
            />
            <TextField
              label="Number of Days"
              type="number"
              value={skuData.numberOfDays}
              onChange={(e) => setSkuData({ ...skuData, numberOfDays: Number(e.target.value) })}
            />
            <TextField
              label="No of People"
              type="number"
              value={skuData.noOfPeople}
              onChange={(e) => setSkuData({ ...skuData, noOfPeople: Number(e.target.value) })}
            />
            <FormControl fullWidth>
              <InputLabel>Slot Size (Hours)</InputLabel>
              <Select
                value={skuData.slotSize ?? ""}
                label="Slot Size (Hours)"
                onChange={(e) => setSkuData({ ...skuData, slotSize: e.target.value === "" ? null : Number(e.target.value) })}
              >
                <MenuItem value="">None</MenuItem>
                {slotSizeOptions.map((v) => (
                  <MenuItem key={v} value={v}>{v} hr{v !== 1 ? "s" : ""}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider />

        {/* ── Selects ── */}
        <Box>
          <SectionLabel>Type Options</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2}>
            <FormControl fullWidth>
              <InputLabel>Coupon Type</InputLabel>
              <Select
                value={skuData.couponType}
                label="Coupon Type"
                onChange={(e) => setSkuData({ ...skuData, couponType: e.target.value })}
              >
                {Object.values(CouponType).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Product Type</InputLabel>
              <Select
                value={skuData.productType}
                label="Product Type"
                onChange={(e) => setSkuData({ ...skuData, productType: e.target.value })}
              >
                {Object.values(ProductType).map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        <Divider />

        {/* ── Radio Groups ── */}
        <Box>
          <SectionLabel>Flags</SectionLabel>
          <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={2} alignItems="flex-start">
            <FormControl>
              <FormLabel>Is Active</FormLabel>
              <RadioGroup
                row
                value={skuData.isActive ? "true" : "false"}
                onChange={(e) => setSkuData({ ...skuData, isActive: e.target.value === "true" })}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Is Renewable</FormLabel>
              <RadioGroup
                row
                value={skuData.isRenewable === null ? "" : skuData.isRenewable ? "true" : "false"}
                onChange={(e) => setSkuData({ ...skuData, isRenewable: e.target.value === "true" })}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Add Time Inclusion / Exclusion Rule</FormLabel>
              <RadioGroup
                row
                value={timeInclusionExclusion ? "true" : "false"}
                onChange={(e) => setTimeInclusionExclusion(e.target.value === "true")}
              >
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
                <FormControlLabel value="false" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Box>
        </Box>

        {/* ── Time Inclusion/Exclusion ── */}
        {timeInclusionExclusion && (
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl>
              <FormLabel>Inclusion / Exclusion</FormLabel>
              <RadioGroup
                row
                value={addTimeInclusion ? "true" : "false"}
                onChange={(e) => setAddTimeInclusion(e.target.value === "true")}
              >
                <FormControlLabel value="true" control={<Radio />} label="Inclusion" />
                <FormControlLabel value="false" control={<Radio />} label="Exclusion" />
              </RadioGroup>
            </FormControl>
            <FormControl sx={{ minWidth: "300px" }}>
              <InputLabel>Time Slots</InputLabel>
              <Select
                multiple
                value={selectedTimeEnums}
                label="Time Slots"
                onChange={(e) => setSelectedTimeEnums(e.target.value)}
                renderValue={(selected: TimeEnum[]) => (
                  <Box sx={{ display: "flex", gap: "0.25rem", flexWrap: "wrap" }}>
                    {selected.map((value) => (
                      <Chip key={value} color="primary" label={value} />
                    ))}
                  </Box>
                )}
              >
                {Object.entries(TimeEnum).map(([key, value]) => (
                  <MenuItem key={key} value={value}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        <Divider />

        <Box>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ minWidth: "200px", padding: "10px 24px", fontSize: "14px", textTransform: "none", borderRadius: "6px" }}
          >
            Create Coupon SKU
          </Button>
        </Box>

      </Box>
    </Create>
  );
};

export default CreateCouponSku;
